import { useState, useEffect } from "react";
import { 
  Box,
  Paper,
  Typography,
  Grow,
  Card,
  CardMedia,
  CardContent,
  Link,
  List,
  ListItem,
  ListItemText,
  Fade
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/system';
import LockIcon from '@mui/icons-material/Lock';

// Local imports
import Error from "../Error/Error";
import Loading from "../Loading";
import { useAuth } from "../AuthContext";
import VideosStepper from "./VideosStepper";
import { ERROR_MESSAGES } from "../../api/constants";
import { api } from "../../api";


const VideosAll = () => {
  const [videos, setVideos] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { isLoggedIn, authLoading } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect( () => {

    // Get video IDs in order
    const fetchData = async() => {
      try {
        const data = await api.get('/videos');
        setVideos(data);
      } catch (err) {
        // TODO: Refactor the workflow if participant don't have any videos assigned
        if (err.message === ERROR_MESSAGES.NOT_FOUND) {
          setError("No videos found.");
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    // Check if user is logged in
    if (!authLoading && !isLoggedIn) {
      setError("You must be logged in to view this page.");
    }

    // Fetch videos if user is logged in
    if (isLoggedIn && !authLoading) {
      fetchData();
    }
  }, [isLoggedIn, authLoading]);

  useEffect(() => {
    // Fetch video data for each video
    if (videos) {
      const fetchAll = async () => {
        try {
          const videoEntries = await Promise.all(
            videos.map(async (video) => {
              const data = await api.get(`/videos/${video.id}`);
              return [video.order, data];
            })
          );
          const dataObj = Object.fromEntries(videoEntries);
          setVideoData(dataObj);
        } catch (err) {
          setError(err.message);
        }
      };
      fetchAll();
    }
  }, [videos]);

  if (error) {
    return <Error message={error} />;
  }

  if (loading || authLoading) {
    return <Loading />;
  }

  return (
    <Fade in timeout={1000}>
      <div className="main">
        <Box sx={{ width: { xs: '100%', sm: '90%', md: '80%' }, margin: 'auto' }}>
          <Typography variant="h5" sx={{ mt: { xs: '1rem', md: '2rem' }, mb: { xs: '1rem', md: '2rem' }, fontWeight: '600', textAlign: 'center' }}>
            {Object.keys(videos).length === 1 ? t('videos.my_video') : t('videos.my_videos')}
          </Typography>
          <Paper elevation={isMobile ? 0 : 2} sx={{ mb: '2rem', padding: { xs: 2, sm: 4 } }}>
            {Object.keys(videos).length === 1 && (
              <Typography variant='body1'>{t('videos.instructions_1')}</Typography>
            )}
            {Object.keys(videos).length > 1 && (
              <>
                <Typography variant='body1'>{t('videos.instructions_2')}</Typography>
                <List sx={{ listStyle: 'decimal'}}>
                  <ListItem sx={{ display: 'list-item', ml: 4 }}>
                    <ListItemText primary={t('videos.step_1')} />
                    <ListItemText secondary={t('videos.step_1_description')} />
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', ml: 4 }}>
                    <ListItemText primary={t('videos.step_2')} />
                    <ListItemText secondary={t('videos.step_2_description')} />
                  </ListItem>
                </List>
                <Typography variant='body1'>{t('videos.instructions_3')}</Typography>
                <Typography variant='body1' sx={{ mt: 2 }}>{t('videos.instructions_4')}</Typography>
              </>
            )}
          </Paper>
          <VideosStepper videos={videos}/>
          {Object.keys(videos).length === 0 && 
            <Box>
              <Typography variant="h6">You don’t have any videos assigned to you.</Typography>
              <Typography variant="h6">Check out <Link href="/vaccine-info">Vaccine Information</Link> page to learn more about HPV Vaccination.</Typography>
            </Box>
          }
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 4, mt: 2 }}>

            {videoData && Object.entries(videoData).map(([order, video]) => {
              let disabled = false;
              if (order != 1 && videos) {
                disabled = !videos.find((v) => v.order === order - 1).completed;
              }
              return (
                <Link 
                  disabled={disabled}
                  aria-disabled={disabled}
                  href={!disabled ? `/videos/${video.id}` : ''}
                  key={order}
                  sx={{
                    width: { xs: '100%', sm: '30%' },
                    height: '100%',
                    '&[disabled]': {
                    pointerEvents: 'none',
                    textDecoration: 'none',
                    opacity: 0.5,
                  }}}
                >
                  <Grow in timeout={700 * order} style={{ transformOrigin: '0 0 0' }}>
                    <Card sx={{ maxWidth: 400, mb: 2, ':hover': {boxShadow: '10px 5px 5px rgb(212, 212, 212)'} }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            alt="video"
                            width="100%"
                            image={video.thumbnail_url}
                          />
                          {disabled &&
                            <LockIcon sx={{ position: 'absolute', top: '40%', left: '40%', padding: '0.5rem', fontSize: '3rem', color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.6)', borderRadius: '50%' }} />
                          }
                        </Box>
                        
                        <CardContent sx={{ minHeight: '100%', height: { xs: '5rem', sm: '7rem' } }}>
                          <Typography variant="h6">{t(`videos.video_${video.id}_title`)}</Typography>
                        </CardContent>
                    </Card>
                  </Grow>
                </Link>
              )
            })
          }
          </Box>
        </Box>
      </div>
    </Fade>
  )
}

export default VideosAll;